/**
 * Breadcrumbs above the panel (the page's "canvas"), replacing the pages' own back links. A page names its trail with
 * useCrumbs([...]); a page that doesn't gets its section ("Meetings"). Labels are the section names from navConfig and
 * the page's own title (a meeting's body and date, a bill's short title, a person's name).
 */
import { Fragment, useLayoutEffect, useSyncExternalStore } from "react";
import { ChevronRight } from "lucide-react";
import { ADMIN_TABS, TABS, adminTabOf } from "../nav/navConfig";
import { navigate, pageKeyOf, plainClick, type Route } from "./router";

export interface Crumb { label: string; href?: string }

let state: { path: string; trail: Crumb[] } | null = null;
const listeners = new Set<() => void>();
const set = (s: typeof state) => { state = s; listeners.forEach((l) => l()); };
const useState_ = () => useSyncExternalStore((l) => { listeners.add(l); return () => { listeners.delete(l); }; }, () => state, () => null);

/** A page's trail (section first, the page last). Pass null while its data isn't there yet. */
export function useCrumbs(trail: Crumb[] | null) {
  const key = trail ? JSON.stringify(trail) : "";
  useLayoutEffect(() => {
    if (!trail) return;
    const mine = { path: pageKeyOf(location.pathname), trail };   // per page: a meeting's or person's tabs keep it
    set(mine);
    return () => { if (state === mine) set(null); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** "Meetings" → its crumb (with its path), for a page's first crumb. */
export const sectionCrumb = (id: keyof typeof TABS): Crumb => ({ label: TABS[id].label, href: TABS[id].path });

const SECTION: Record<string, keyof typeof TABS> = { "": "overview", overview: "overview", meetings: "meetings", people: "people", organizations: "organizations",
  legislation: "legislation", "forecast-method": "legislation", analytics: "analytics", settings: "settings", admin: "approvals" };

export function Breadcrumbs({ route }: { route: Route }) {
  const s = useState_();
  const section = SECTION[route.section];
  // The admin dashboard (staff): "Admin › Organizations"; Approvals is the admin home.
  const adminTab = route.section === "admin" ? ADMIN_TABS.find((t) => t.id === adminTabOf(route.path)) : undefined;
  const trail = s && s.path === pageKeyOf(route.path) ? s.trail
    : adminTab ? [adminTab.id === "approvals" ? { label: TABS.approvals.label } : { label: TABS.approvals.label, href: TABS.approvals.path }, { label: adminTab.label }]
    : section ? [{ label: TABS[section].label }] : [];
  if (!trail.length) return <div className="crumbs-bar" />;
  return (
    <nav className="crumbs-bar" aria-label="Breadcrumb">
      <ol>
        {trail.map((c, i) => {
          const last = i === trail.length - 1;
          return (
            <Fragment key={`${i}-${c.label}`}>
              {i > 0 && <li aria-hidden className="crumbs-bar__sep"><ChevronRight size={14} /></li>}
              <li className="crumbs-bar__item">
                {!last && c.href
                  ? <a href={c.href} onClick={(e) => { if (!plainClick(e)) return; e.preventDefault(); navigate(c.href!); }}>{c.label}</a>
                  : <span aria-current={last ? "page" : undefined}>{c.label}</span>}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

/** The same as useCrumbs, as an element: put it where a page's back link used to be. */
export function Crumbs({ trail }: { trail: Crumb[] }) {
  useCrumbs(trail);
  return null;
}
